/**
 * Main for a head-to-head game (docs/contest-ux.md, Revision 1):
 *
 *   GameLinesCard     one wide sportsbook table, a row per team (+ Draw for a 3-way result) and
 *                     the columns Moneyline · Spread · Total. Spread and Total show their main
 *                     line with a ‹ › stepper in the column header; a step moves both rows'
 *                     cells together. Below a 40rem card width it stacks into Moneyline, Spread
 *                     and Total groups (each with both teams) so nothing overflows at 375px.
 *   PopularPropsCard  up to 6 players' Points lines (or first stat): O/U line · Over · Under,
 *                     then "All props →".
 *
 * Every cell is a `QuoteCell`: best price + venue, tap to trade, inline "+N" for every venue
 * (inline list under the card below 1280px; the insight column at 1280px+). The column titles
 * (Moneyline · Spread · Total), the shown line in a column's stepper and each popular prop's
 * "Points · O/U 24.5" link to that market's page (`ctx.marketLink`).
 */
import { useState, type ReactNode } from "react";
import {
  lineCell,
  resolveRung,
  stepRung,
  type GameLines,
  type GameSide,
  type LineColumn,
  type PopularProp,
} from "../../lib/contestMarkets.js";
import { sideColumnLabel, type PositionLike } from "../../lib/books.js";
import { Pill, SoftCard } from "../kit.js";
import { VenueLogo } from "../VenueLogo.js";
import {
  lineLabel,
  MarketLink,
  PanelBody,
  PanelToggles,
  QuoteCell,
  rowSideLabel,
  rowTitle,
  signed,
  Stepper,
  VenueList,
  type CardCtx,
  type Panel,
} from "./MarketCards.js";

const sideKeyOf = (p: PositionLike) => (p.side_key ?? "").toLowerCase();

/** A cell's own line: "+1.5" / "−1.5" for a spread side, "O 241.5" / "U 241.5" for a total. */
function cellLine(p: PositionLike | null, kind: "spread" | "total"): string | undefined {
  if (!p || p.variable == null || p.variable === "") return undefined;
  const v = Number(p.variable);
  if (!Number.isFinite(v)) return undefined;
  if (kind === "spread") return signed(v);
  return `${sideKeyOf(p) === "under" ? "U" : "O"} ${Math.round(v * 10) / 10}`;
}

/** One stepped column (Spread / Total): its shown line, the stepper, and the insight title. */
function useColumn(col: LineColumn | null, ctx: CardCtx) {
  const [key, setKey] = useState<string | null>(null);
  if (!col) return null;
  const index = resolveRung(col.rungs, key, col.mainIndex);
  const rung = col.rungs[index];
  const label = lineLabel(rung, col.kind, ctx.teams);
  const href = ctx.marketLink(rung?.sides ?? []);
  const stepper =
    col.rungs.length > 1 ? (
      <Stepper
        index={index}
        count={col.rungs.length}
        label={label}
        onStep={(d) => setKey(col.rungs[stepRung(col.rungs.length, index, d)].key)}
        onMain={() => setKey(null)}
        atMain={index === col.mainIndex}
        href={href}
      />
    ) : (
      <MarketLink href={href} tap className="nums text-[13px] font-medium text-ink">
        {label}
      </MarketLink>
    );
  return {
    col,
    index,
    slice: rung?.sides ?? [],
    title: `${col.title} · ${label}`,
    href,
    cell: (side: GameSide) => lineCell(col, index, side),
    stepper,
  };
}

type Column = NonNullable<ReturnType<typeof useColumn>>;

/** A column's title (a quiet link to its market when it has one) and its stepper. */
function ColumnHead({ title, href, right }: { title: string; href?: string | null; right?: ReactNode }) {
  return (
    <div className="flex min-h-8 min-w-0 flex-wrap items-center gap-x-1 gap-y-0.5">
      <MarketLink href={href} tap className="min-w-0 truncate text-[11px] font-semibold uppercase tracking-wider text-dim">
        {title}
      </MarketLink>
      {right}
    </div>
  );
}

const Empty = () => (
  <div className="flex min-h-11 items-center justify-center rounded-md border border-dashed border-border/60 text-[12px] text-faint" aria-hidden>
    —
  </div>
);

export function GameLinesCard({
  gl,
  ctx,
  highlight,
}: {
  gl: GameLines;
  ctx: CardCtx;
  highlight: boolean;
}) {
  const spread = useColumn(gl.spread, ctx);
  const total = useColumn(gl.total, ctx);
  const [openHash, setOpenHash] = useState<string | null>(null);
  // Below 1280px: the Book / Chart toggles, for the market picked under them (default moneyline).
  const [panel, setPanel] = useState<Panel>(null);
  const [panelCol, setPanelCol] = useState<string>("ml");
  const cols = [spread, total].filter((c): c is Column => !!c);

  const mlSlice = gl.rows.map((r) => r.moneyline).filter((p): p is PositionLike => !!p);
  const mlTitle = gl.moneyline.title;
  const mlHref = ctx.marketLink(mlSlice);

  const toggle = (p: PositionLike) => {
    setOpenHash((h) => (h === p.position_hash ? null : p.position_hash));
    // The Book / Chart follow the column whose venue list was opened.
    const c = cols.find((x) => x.slice.some((s) => s.position_hash === p.position_hash));
    setPanelCol(c ? c.col.cardKey : "ml");
  };
  const picked = cols.find((c) => c.col.cardKey === panelCol);
  const panelSlice = picked ? picked.slice : mlSlice;
  const panelCaption = picked ? picked.title : mlTitle;
  const cell = (p: PositionLike | null, market: { title: string; slice: PositionLike[] }, label?: string) =>
    p ? (
      <QuoteCell
        key={p.position_hash}
        pos={p}
        label={label}
        ctx={ctx}
        market={market}
        listOpen={openHash === p.position_hash}
        onToggleList={() => toggle(p)}
        className="h-full"
      />
    ) : (
      <Empty />
    );

  // The open venue list (below 1280px), under the table / under its group.
  const every = [...mlSlice, ...cols.flatMap((c) => c.slice)];
  const open = ctx.wide ? null : (every.find((p) => p.position_hash === openHash) ?? null);
  const openLabel = (p: PositionLike) => sideColumnLabel(p, sideKeyOf(p));
  const listFor = (slice: PositionLike[]) =>
    open && slice.some((p) => p.position_hash === open.position_hash) ? (
      <VenueList pos={open} label={openLabel(open)} ctx={ctx} />
    ) : null;

  // Static strings so Tailwind sees them: team · Moneyline (· Spread) (· Total).
  const tableCols = [
    "grid-cols-[minmax(7rem,1.15fr)_minmax(0,1fr)]",
    "grid-cols-[minmax(7rem,1.15fr)_repeat(2,minmax(0,1fr))]",
    "grid-cols-[minmax(7rem,1.15fr)_repeat(3,minmax(0,1fr))]",
  ][cols.length];

  return (
    <div data-card={gl.moneyline.cardKey} className="min-w-0 scroll-mt-28">
      <SoftCard
        className={`@container flex flex-col gap-3 p-3 transition-shadow duration-300 motion-reduce:transition-none lg:p-4 ${
          highlight ? "ring-2 ring-amber/60" : ""
        }`}
      >
        <h3 className="text-[14px] font-semibold leading-snug text-ink">Game lines</h3>

        {/* ≥40rem: the sportsbook table */}
        <div className="hidden flex-col gap-2 @[40rem]:flex" role="table" aria-label="Game lines">
          <div className={`grid items-end gap-2 ${tableCols}`} role="row">
            <span role="columnheader" className="sr-only">
              Team
            </span>
            <div role="columnheader">
              <ColumnHead title={mlTitle} href={mlHref} />
            </div>
            {cols.map((c) => (
              <div key={c.col.cardKey} role="columnheader">
                <ColumnHead title={c.col.title} href={c.href} right={c.stepper} />
              </div>
            ))}
          </div>
          {gl.rows.map((r) => (
            <div key={r.side} className={`grid items-stretch gap-2 ${tableCols}`} role="row">
              <div role="rowheader" className="flex min-h-11 min-w-0 items-center gap-2">
                {r.side !== "draw" && <VenueLogo name={r.label} size={22} />}
                <span className="min-w-0 break-words text-[14px] font-medium leading-snug text-ink">{r.label}</span>
              </div>
              <div role="cell" className="min-w-0">
                {cell(r.moneyline, { title: mlTitle, slice: mlSlice })}
              </div>
              {cols.map((c) => (
                <div key={c.col.cardKey} role="cell" className="min-w-0">
                  {r.side === "draw" ? <Empty /> : cell(c.cell(r.side), { title: c.title, slice: c.slice }, cellLine(c.cell(r.side), c.col.kind))}
                </div>
              ))}
            </div>
          ))}
          {listFor(every)}
        </div>

        {/* <40rem: Moneyline, Spread, Total groups, each with both teams */}
        <div className="flex flex-col gap-3 @[40rem]:hidden">
          <section className="flex flex-col gap-2">
            <ColumnHead title={mlTitle} href={mlHref} />
            <div className={`grid gap-2 ${gl.rows.length > 2 ? "grid-cols-1" : "grid-cols-1 @[22rem]:grid-cols-2"}`}>
              {gl.rows.map((r) => (
                <div key={r.side} className="min-w-0">
                  {cell(r.moneyline, { title: mlTitle, slice: mlSlice }, r.label)}
                </div>
              ))}
            </div>
            {listFor(mlSlice)}
          </section>
          {cols.map((c) => (
            <section key={c.col.cardKey} className="flex flex-col gap-2 border-t border-border/70 pt-3">
              <div className="flex min-w-0 items-center justify-between gap-2">
                <MarketLink href={c.href} tap className="min-w-0 truncate text-[11px] font-semibold uppercase tracking-wider text-dim">
                  {c.col.title}
                </MarketLink>
                {c.stepper}
              </div>
              <div className="grid grid-cols-1 gap-2 @[22rem]:grid-cols-2">
                {gl.rows
                  .filter((r) => r.side !== "draw")
                  .map((r) => {
                    const p = c.cell(r.side);
                    const label = c.col.kind === "spread" ? `${r.label} ${cellLine(p, "spread") ?? ""}`.trim() : p ? sideColumnLabel(p, sideKeyOf(p)) : r.label;
                    return (
                      <div key={r.side} className="min-w-0">
                        {cell(p, { title: c.title, slice: c.slice }, label)}
                      </div>
                    );
                  })}
              </div>
              {listFor(c.slice)}
            </section>
          ))}
        </div>

        {/* ≥1280px the insight column shows the book and chart instead. */}
        {!ctx.wide && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/70 pt-2">
              <PanelToggles panel={panel} onPanel={setPanel} />
              {panel && cols.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Market for the book / chart">
                  <Pill active={panelCol === "ml"} onClick={() => setPanelCol("ml")}>
                    {mlTitle}
                  </Pill>
                  {cols.map((c) => (
                    <Pill key={c.col.cardKey} active={panelCol === c.col.cardKey} onClick={() => setPanelCol(c.col.cardKey)}>
                      {c.col.title}
                    </Pill>
                  ))}
                </div>
              )}
            </div>
            <PanelBody panel={panel} slice={panelSlice} caption={panelCaption} ctx={ctx} />
          </>
        )}
      </SoftCard>
    </div>
  );
}

export function PopularPropsCard({
  props,
  ctx,
  onAll,
}: {
  props: PopularProp[];
  ctx: CardCtx;
  onAll: () => void;
}) {
  const [openHash, setOpenHash] = useState<string | null>(null);
  if (props.length === 0) return null;
  return (
    <SoftCard className="@container flex flex-col gap-2 p-3 lg:p-4">
      <div className="flex min-h-8 items-center gap-2">
        <h3 className="min-w-0 flex-1 text-[14px] font-semibold leading-snug text-ink">Popular props</h3>
        <button
          type="button"
          onClick={onAll}
          className="min-h-11 shrink-0 rounded-full px-2 text-[12px] font-medium text-dim hover:text-amber lg:min-h-8"
        >
          All props →
        </button>
      </div>
      <div className="flex flex-col divide-y divide-border/70">
        {props.map((pp) => {
          const rung = pp.row.rungs[pp.row.mainIndex];
          const sides = rung?.sides ?? [];
          const title = rowTitle({ title: pp.player, rowKind: "lines" }, pp.row, rung);
          const open = ctx.wide ? null : sides.find((s) => s.position_hash === openHash);
          return (
            <div key={pp.cardKey} className="flex flex-col gap-2 py-2 first:pt-0 last:pb-0">
              <div className="grid grid-cols-1 items-center gap-2 @[34rem]:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)]">
                <div className="flex min-h-11 min-w-0 items-center gap-2">
                  <VenueLogo name={pp.player} logoUrl={pp.imageUrl} size={28} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-medium text-ink">{pp.player}</div>
                    <div className="nums flex min-w-0 items-center text-[12px] text-dim">
                      <MarketLink href={ctx.marketLink(sides)} tap className="min-w-0 truncate">
                        {pp.row.label}
                        {rung?.line != null ? ` · O/U ${rung.line}` : ""}
                      </MarketLink>
                      <span className="shrink-0 whitespace-pre text-faint"> · {pp.venues} venue{pp.venues === 1 ? "" : "s"}</span>
                    </div>
                  </div>
                </div>
                <div className={`grid min-w-0 gap-2 @[34rem]:contents ${sides.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
                  {sides.map((s) => (
                    <QuoteCell
                      key={s.position_hash}
                      pos={s}
                      label={rowSideLabel(s)}
                      ctx={ctx}
                      market={{ title, slice: sides }}
                      listOpen={openHash === s.position_hash}
                      onToggleList={() => setOpenHash((h) => (h === s.position_hash ? null : s.position_hash))}
                    />
                  ))}
                </div>
              </div>
              {open && <VenueList pos={open} label={`${pp.player} ${rowSideLabel(open)}`} ctx={ctx} />}
            </div>
          );
        })}
      </div>
    </SoftCard>
  );
}
